import { PayChannel, SubscriptionCapability } from "@features/api/memberApi";
import { PAYMENT_FAILED } from "@utils/paymentErrorUtils";
import type { SubscriptionListItem } from "./types";

/**
 * 订阅管理动作推导（EDG-552，fe-spec §4.6 F5）。纯函数，便于逐行覆盖 F5 状态表。
 *
 * 管理按钮由后端 `capabilities` 决定（C-5：后端加渠道前端不改），不按渠道写死；
 * `capabilities` 缺失（老版本响应）时返回 legacy，UI 回退按渠道的旧渲染（AC-33）。
 */
export type SubscriptionAction =
  /** HOSTED_PORTAL → 外跳渠道门户（STRIPE customer_session_url） */
  | "manage_portal"
  /** CANCEL_AT_PERIOD_END 且续费中 → 挽留 → 问卷 → PATCH auto_renew=false */
  | "turn_off"
  /** RESUME 且已关续费 → PATCH auto_renew=true */
  | "resume"
  /** UPDATE_PAYMENT_METHOD 且可外跳 → 换卡托管页（Slice 5 渲染） */
  | "update_pm"
  /** 外跳不可用（旧版 App / 远程降级）或未知渠道：不可点提示「Manage on edwealth.ai」 */
  | "manage_on_web";

export type SubscriptionSubtitle =
  "renews" | "ends" | "payment_failed" | "legacy";

export interface SubscriptionActionEnv {
  /** 可外跳：非 App，或 App 已声明 openUrlExternal 且未被远程降级 */
  canOpenExternal: boolean;
}

export interface SubscriptionActions {
  /** 主动作；"menu" = 主按钮打开菜单；null 且 subtitle=legacy → 整行走旧渲染 */
  primary: SubscriptionAction | "menu" | null;
  menu: SubscriptionAction[];
  subtitle: SubscriptionSubtitle;
}

/** 能力为空集时仍走旧渲染的已知渠道（PAYPAL autopay 外跳 / IAP 应用商店，后端能力即空集） */
const LEGACY_CHANNELS: ReadonlySet<string> = new Set([
  PayChannel.PAYPAL,
  PayChannel.APPLE_IAP,
  PayChannel.GOOGLE_IAP,
  PayChannel.WALLET,
  PayChannel.STRIPE,
]);

const LEGACY: SubscriptionActions = {
  primary: null,
  menu: [],
  subtitle: "legacy",
};

function externalActions(
  capabilities: SubscriptionCapability[],
  canOpenExternal: boolean,
): SubscriptionAction[] {
  // 站内发票列表本期不做（be-design v1.4），依赖外跳的只剩换卡
  if (!capabilities.includes(SubscriptionCapability.UPDATE_PAYMENT_METHOD)) {
    return [];
  }
  return canOpenExternal ? ["update_pm"] : ["manage_on_web"];
}

/** 已关续费：能恢复 → Resume；否则有外跳项 → 打开菜单；都没有 → 不渲染 */
function resolveEndsPrimary(
  canResume: boolean,
  externals: SubscriptionAction[],
): SubscriptionActions["primary"] {
  if (canResume) {
    return "resume";
  }
  return externals.length ? "menu" : null;
}

function buildCapabilityActions(
  item: SubscriptionListItem,
  capabilities: SubscriptionCapability[],
  env: SubscriptionActionEnv,
): SubscriptionActions {
  const externals = externalActions(capabilities, env.canOpenExternal);
  const isAutoRenewOff = item.autoRenew === false;
  const canTurnOff =
    !isAutoRenewOff &&
    capabilities.includes(SubscriptionCapability.CANCEL_AT_PERIOD_END);
  const canResume =
    isAutoRenewOff && capabilities.includes(SubscriptionCapability.RESUME);

  if (item.paymentIssue === PAYMENT_FAILED) {
    const primary = externals.includes("update_pm") ? "update_pm" : "menu";
    const menu: SubscriptionAction[] = [
      ...(canTurnOff ? (["turn_off"] as const) : []),
      ...externals.filter((action) => action !== primary),
    ];
    return { primary, menu, subtitle: "payment_failed" };
  }
  if (isAutoRenewOff) {
    return {
      primary: resolveEndsPrimary(canResume, externals),
      menu: externals,
      subtitle: "ends",
    };
  }
  const menu: SubscriptionAction[] = [
    ...(canTurnOff ? (["turn_off"] as const) : []),
    ...externals,
  ];
  return { primary: menu.length ? "menu" : null, menu, subtitle: "renews" };
}

export function buildSubscriptionActions(
  item: SubscriptionListItem,
  env: SubscriptionActionEnv,
): SubscriptionActions {
  const { capabilities } = item;
  if (!capabilities) {
    return LEGACY;
  }
  if (capabilities.includes(SubscriptionCapability.HOSTED_PORTAL)) {
    return { primary: "manage_portal", menu: [], subtitle: "legacy" };
  }
  if (!capabilities.length) {
    return LEGACY_CHANNELS.has(item.pay_channel)
      ? LEGACY
      : { primary: "manage_on_web", menu: [], subtitle: "legacy" };
  }
  return buildCapabilityActions(item, capabilities, env);
}
